import { collection, deleteDoc, doc, getDocs, setDoc } from 'firebase/firestore'
import React from 'react'
import { UserDataTypes } from 'src/pages/members/User/Mangement/UserManagement'
import { db, getAllAccessLevels } from 'src/services/firebase-compat'
import { BName, BnamesService, DefaultService, OpenAPI, skipAbortController, SubChainData, SubChainSetRequest } from 'src/services/openApiV2'
import { sendSlackNotification } from 'src/utils/api'
import { get } from 'src/utils/config/lodashUtils'
import { isInternalUser, isLoopUser } from 'src/utils/functions'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { v4 as uuid } from 'uuid'
import { useAuth } from './AuthContext'
import { AccessLevelEnum, UserType } from './AuthContext.type'
import { useErrorData } from './ErrorContext'

export interface SubChainModules {
  created_at?: string
  created_by?: string
  updated_at?: string
  updated_by?: string
  modules: { uid: string; type?: 'preview' | 'live' }[]
  name: string
  uid?: string
}

export interface SubChainOrgUser {
  [user_uid: string]: SubChainData[]
}

interface SubChainContextInterface {
  userAlreadyMappedToOtherSubChain: (uid: string, subChainName: string) => boolean
  createSubChain: (obj: { subChainName: string; selectedLocationIds: number[]; selectedUsers: UserType[]; editData?: SubChainData }) => Promise<void>
  mapUsersToSubChains: (sub_chains: string[], user_id_list: string[]) => Promise<void>
  getAllSubChains: () => Promise<SubChainData[]>
  removeUsersFromSubChains: (user_id_list: string[]) => Promise<void>
  getAllUserAndSubChainRelation: () => SubChainOrgUser
  currentUserSubChainList: SubChainData[]
  allUsersAndSubChainRelation: {
    [uid: string]: SubChainData[]
  }
  allSubChains: SubChainData[]
  currentUserAccessibleSubChainList: SubChainData[]
  initialLoading: boolean
  getFilteredUserList: (allUsersData: UserType[] | UserDataTypes[], isDemo: boolean) => UserDataTypes[]
  deleteSubChain: (subChainName: string) => Promise<void>
  getSubchainOrgUsers: (org?: string) => Promise<SubChainOrgUser>
  getSubchainModules: () => Promise<{
    [uid: string]: SubChainModules
  }>
  setSubchainModules: (data: Partial<SubChainModules>) => Promise<void>
  chainUsersLocations: { [uid: string]: BName[] }
  chainUsersLocationsLoading: boolean
}

const SubChainContext = React.createContext({} as SubChainContextInterface)

export const useSubChainContext = () => React.useContext(SubChainContext)

export default function SubChainContextProvider(props: { children: React.ReactNode }) {
  const { currentUser, subChainData } = useAuth()
  const { handleError } = useErrorData()
  const [allUsersAndSubChainRelation, setAllUsersAndSubChainRelation] = React.useState<{ [uid: string]: SubChainData[] }>({})
  const [allSubChains, setAllSubChains] = React.useState<SubChainData[]>([])
  const [initialLoading, setInitialLoading] = React.useState(true)
  const [chainUsersLocations, setChainUsersLocations] = React.useState<{ [uid: string]: BName[] }>({})
  const [chainUsersLocationsLoading, setChainUsersLocationsLoading] = React.useState(true)

  const currentUserSubChainList = React.useMemo(() => {
    const subChainList = get(allUsersAndSubChainRelation, currentUser?.uid, [] as SubChainData[])
    if (!Array.isArray(subChainList)) {
      return []
    }
    return subChainList
  }, [currentUser, allUsersAndSubChainRelation])

  const currentUserAccessibleSubChainList = React.useMemo(() => {
    const currentUserAccessLevel = getAccessLevel(currentUser)

    if (currentUserAccessLevel === AccessLevelEnum.BUSINESS_ADMIN) {
      return allSubChains
    }

    // For franchise_access store managers and all other non-admin roles, filter by their assigned subchains.
    return allSubChains.filter((item) => currentUserSubChainList.some((e) => e.id === item.id))
  }, [currentUserSubChainList, allSubChains, currentUser])

  const userAlreadyMappedToOtherSubChain = React.useCallback(
    (uid: string, subChainName: string) => {
      const subChains = get(allUsersAndSubChainRelation, uid, [])
      return Array.isArray(subChains) && subChains.length > 0 && subChains.some((e) => e.name !== subChainName)
    },
    [allUsersAndSubChainRelation]
  )

  const getAllSubChains = React.useCallback(async () => {
    try {
      const userAccessLevel = getAccessLevel(currentUser)

      // Include ALL Store Managers to show franchise filter
      const allowedAccessLevels = [
        AccessLevelEnum.BUSINESS_ADMIN,
        AccessLevelEnum.BUSINESS_MANAGER,
        AccessLevelEnum.INTERNAL_OPERATOR,
        AccessLevelEnum.STORE_MANAGER // All Store Managers can fetch subchains
      ]

      if (!allowedAccessLevels.includes(userAccessLevel)) {
        setAllSubChains([])
        return []
      }
      const res = await DefaultService.getSubchainsV2SubchainGetAllV2Get({ chain: get(currentUser, 'org', '') })
      setAllSubChains(res)
      return res
    } catch (err) {
      handleError(err.message)
      return []
    }
  }, [currentUser])

  const getAllUserAndSubChainRelation = React.useCallback(() => {
    if (Object.keys(allUsersAndSubChainRelation).length === 0) {
      setInitialLoading(true)
    }
    let result: { [uid: string]: SubChainData[] } = {}
    try {
      if (!subChainData) {
        return result
      }
      if (currentUser?.org) {
        result = subChainData
      } else {
        return {}
      }

      for (const key in result) {
        result[key] = result[key].map((e) => allSubChains.find((item) => item.id === e.id)).filter((e) => !!e)
      }
      setAllUsersAndSubChainRelation(result)

      return result
    } catch (err) {
      handleError(err.message)
      return result
    } finally {
      setInitialLoading(false)
    }
  }, [allUsersAndSubChainRelation, allSubChains, currentUser?.org, subChainData])

  const mapUsersToSubChains = React.useCallback(
    async (sub_chains: string[], user_id_list: string[]) => {
      try {
        const res = await DefaultService.mapUsersToSubchainsSubchainMapUserPost({
          chain: get(currentUser, 'org', ''),
          requestBody: {
            user_id_list,
            sub_chains
          }
        })
      } catch (err) {
        throw err
      }
    },
    [currentUser?.org]
  )

  const removeUsersFromSubChains = React.useCallback(
    async (user_id_list: string[]) => {
      try {
        const res = await DefaultService.mapUsersToSubchainsSubchainMapUserPost({
          chain: get(currentUser, 'org', ''),
          requestBody: {
            user_id_list,
            sub_chains: []
          }
        })
      } catch (err) {
        throw err
      }
    },
    [currentUser?.org]
  )

  const createSubChain = React.useCallback(
    async (obj: { subChainName: string; selectedLocationIds: number[]; selectedUsers: UserType[]; editData?: SubChainData }) => {
      const { subChainName, selectedLocationIds, selectedUsers, editData } = obj
      // ? to be added later
      // await createFirebaseSubChain(id)
      const prevData = (
        editData
          ? {
              name: editData.name,
              location_ids: editData.bnames?.map((e) => e.id) || [],
              user_ids: editData.users?.map((e) => e.user_id) || [],
              accounting_company_ids: editData.accounting_companies?.map((e) => e.company_id) || []
            }
          : {
              name: subChainName
            }
      ) as SubChainSetRequest
      await DefaultService.createSubchainSubchainSetPost({
        chain: get(currentUser, 'org', ''),
        requestBody: {
          ...prevData,
          name: subChainName,
          location_ids: selectedLocationIds,
          user_ids: selectedUsers.map((e) => e.uid)
        }
      })

      // await setSubchainModules({ name: subChainName, modules: categorizedRoutes.map((e) => ({ uid: e.category.key, type: 'live' })) })
      sendSlackNotification({
        message: `Sub-chain created by ${currentUser?.email}`,
        channel: 'fe-logs',
        title: `Sub-chain created:
      By: \`${currentUser?.email}\`
      User's Access Level: \`${getAccessLevel(currentUser)}\`
      For Org: \`${currentUser?.org}\`
      Name: \`${subChainName}\`
      `
      })
    },
    [currentUser?.email, currentUser?.org]
  )

  const deleteSubChain = async (subChainName: string) => {
    await DefaultService.deleteSubchainSubchainDeleteDelete({ chain: currentUser?.org, name: subChainName })
    await deleteDoc(doc(db, 'subchain_modules', subChainName))
    sendSlackNotification({
      message: `Sub-chain deleted by ${currentUser.email}`,
      channel: 'fe-logs',
      title: `Sub-chain deleted:
      By: \`${currentUser.email}\`
      User's Access Level: \`${getAccessLevel(currentUser)}\`
      For Org: \`${currentUser.org}\`
      Name: \`${subChainName}\`
      `
    })
  }

  const getSubchainModules = async () => {
    try {
      const res = await getDocs(collection(db, 'subchain_modules'))
      return res.docs
        .map((doc) => ({ uid: doc.id, ...doc.data() }) as SubChainModules)
        .reduce((acc, curr) => ({ ...acc, [curr.name]: curr }), {} as { [uid: string]: SubChainModules })
    } catch (err) {
      throw err
    }
  }

  const setSubchainModules = async (data: Partial<SubChainModules>) => {
    try {
      if (!data?.name) {
        handleError('Subchain is required')
        return
      }
      const uid = data.uid || uuid()
      await setDoc(doc(db, 'subchain_modules', uid), {
        ...data,
        uid: uid,
        org: currentUser?.org,
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.email
      })
    } catch (err) {
      handleError('Something went wrong while setting modules')
    }
  }

  const getChainUsersLocations = async () => {
    setChainUsersLocationsLoading(true)
    try {
      const res = (await BnamesService.getChainUsersBnamesChainUsersBnamesGet()) as { [uid: string]: BName[] }
      setChainUsersLocations(res)
    } catch (err) {
      handleError(err.message)
    } finally {
      setChainUsersLocationsLoading(false)
    }
  }

  const getFilteredUserList = React.useCallback(
    (allUsersData: UserType[] | UserDataTypes[], isDemo: boolean) => {
      if (chainUsersLocationsLoading || initialLoading) return []

      const arr = allUsersData || []
      const tempData = arr.filter((item) => !isLoopUser(item) && !isInternalUser(item))
      const loopUsers = arr.filter((item) => item?.internal)
      const tempUserList = currentUser?.internal ? arr : !isDemo ? tempData : loopUsers

      if (getAccessLevel(currentUser) === AccessLevelEnum.BUSINESS_ADMIN) {
        return tempUserList
      }

      const currentUserSubChainList = get(allUsersAndSubChainRelation, currentUser?.uid, [] as SubChainData[]) || []
      const userAccessLevel = getAccessLevel(currentUser)

      const isUserInSameSubChain = (item: UserType | UserDataTypes) => {
        const userSubChains = get(allUsersAndSubChainRelation, item.uid, []) as SubChainData[]
        return (
          Array.isArray(userSubChains) &&
          userSubChains.some((subChain) => currentUserSubChainList.some((currentSubChain) => currentSubChain.id === subChain.id))
        )
      }

      const hasValidAccessLevel = (item: UserType | UserDataTypes) => {
        const accessLevel = get(item, 'access_level', AccessLevelEnum.BUSINESS_ADMIN)
        const accessLevelArray = Array.isArray(accessLevel) ? accessLevel : [accessLevel]
        const isCustom = accessLevelArray.some((level) => getAllAccessLevels(level)?.is_custom)

        return (
          isCustom ||
          accessLevelArray.includes(AccessLevelEnum.STORE_MANAGER) ||
          accessLevelArray.includes(AccessLevelEnum.OBSERVER) ||
          accessLevelArray.includes(AccessLevelEnum.INTERNAL_OPERATOR)
        )
      }

      const isUserInSharedLocation = (item: UserType | UserDataTypes) => {
        return currentUserSubChainList.some((subchain) => {
          const subchainLocationIds = subchain.bnames?.map((location) => location.id) || []
          const userLocationIds = chainUsersLocations[item.uid]?.map((location) => location.id) || []

          return userLocationIds.length > 0 && userLocationIds.every((userLocationId) => subchainLocationIds.includes(userLocationId))
        })
      }

      if ([AccessLevelEnum.INTERNAL_OPERATOR, AccessLevelEnum.OWNER_VIEW, AccessLevelEnum.BUSINESS_MANAGER].includes(userAccessLevel)) {
        return tempUserList.filter(
          (item) =>
            item?.uid === currentUser?.uid ||
            (isUserInSameSubChain(item) && hasValidAccessLevel(item)) ||
            (isUserInSharedLocation(item) && hasValidAccessLevel(item))
        )
      }

      return tempUserList.filter((item) => item?.uid === currentUser?.uid || isUserInSameSubChain(item))
    },
    [allUsersAndSubChainRelation, currentUser, chainUsersLocationsLoading, chainUsersLocations, initialLoading]
  )

  React.useEffect(() => {
    const getData = async () => {
      if (
        currentUser &&
        currentUser?.org &&
        [AccessLevelEnum.BUSINESS_ADMIN, AccessLevelEnum.BUSINESS_MANAGER, AccessLevelEnum.INTERNAL_OPERATOR].includes(getAccessLevel(currentUser))
      ) {
        getAllSubChains()
      } else {
        setInitialLoading(false)
      }
    }
    getData()
  }, [currentUser?.org, getAccessLevel(currentUser), getAllSubChains])

  React.useEffect(() => {
    const getData = async () => {
      if (
        currentUser &&
        currentUser?.org &&
        [AccessLevelEnum.BUSINESS_ADMIN, AccessLevelEnum.BUSINESS_MANAGER, AccessLevelEnum.INTERNAL_OPERATOR].includes(getAccessLevel(currentUser))
      ) {
        if (allSubChains.length > 0) {
          getAllUserAndSubChainRelation()
        } else {
          // If no subchains, still set loading to false
          setInitialLoading(false)
        }
      }
    }
    getData()
  }, [currentUser?.org, getAccessLevel(currentUser), getAllUserAndSubChainRelation, allSubChains])

  React.useEffect(() => {
    if (OpenAPI?.TOKEN) getChainUsersLocations()
  }, [OpenAPI?.TOKEN])

  const contextValue = React.useMemo(() => {
    return {
      userAlreadyMappedToOtherSubChain,
      createSubChain,
      mapUsersToSubChains,
      getAllSubChains,
      removeUsersFromSubChains,
      getAllUserAndSubChainRelation,
      deleteSubChain,
      getSubchainOrgUsers,
      getSubchainModules,
      setSubchainModules,
      allUsersAndSubChainRelation,
      currentUserAccessibleSubChainList,
      allSubChains,
      initialLoading,
      currentUserSubChainList,
      getFilteredUserList,
      chainUsersLocations,
      chainUsersLocationsLoading
    }
  }, [
    userAlreadyMappedToOtherSubChain,
    createSubChain,
    mapUsersToSubChains,
    getAllSubChains,
    removeUsersFromSubChains,
    getAllUserAndSubChainRelation,
    allUsersAndSubChainRelation,
    allSubChains,
    currentUserAccessibleSubChainList,
    initialLoading,
    currentUserSubChainList,
    getFilteredUserList,
    chainUsersLocations,
    chainUsersLocationsLoading
  ])

  return <SubChainContext.Provider value={contextValue}>{props.children}</SubChainContext.Provider>
}

export async function getSubchainOrgUsers(org: string, skipAbort?: boolean) {
  try {
    const func = skipAbort ? skipAbortController(DefaultService.getAllUserSubChainsSubchainOrgUsersGet) : DefaultService.getAllUserSubChainsSubchainOrgUsersGet
    const res = await func({ chain: org })
    return res as SubChainOrgUser
  } catch (err) {
    throw err
  }
}
